// src/top50/admin.js — Top 50 admin tab (upload, parse, preview, commit, history)
//
// Path-to-8 Stage 5 / Phase 2b (May 6 2026): the ~360 lines that were
// inline at admin.html:12848-13207, extracted here as ES module functions.
//
// Flow:
//   1. User picks .xls file (drop zone wiring in wireDropZone)
//   2. SheetJS parses both Value + Volume sheets in browser
//   3. Frontend extracts date range from cell D1, store names from row 0,
//      and per-product / per-store data from row 4 onwards
//   4. Preview rendered — user reviews and confirms
//   5. POST /api/top50/upload sends parsed JSON to Worker
//   6. Worker upserts (deletes existing report for that week, inserts new)
//
// Why parse in browser: keeps Worker simple (no .xls library at edge),
// gives user a preview before commit, and the file never leaves the
// browser as a binary upload.
//
// Free variables: XLSX (SheetJS, loaded via CDN in admin.html head — sets
// itself on window.XLSX explicitly, so the `window.` prefix is correct
// for it) and DB (database helper from db.js, declared as `const DB`
// at top level — accessible as a bare identifier from sibling classic
// scripts including this IIFE bundle, but NOT as window.DB). Both are
// looked up at runtime when the relevant functions are called.
//
// v80.12 → v80.13 (May 2026): the admin upload/list/delete URLs were
// originally `WORKER_URL + '/api/...'` referencing a bare global that
// was never defined anywhere — pre-existing bug from v80.2 that nobody
// noticed because the only upstream caller (commitTop50Upload) was
// caught by try/catch and shown as a generic error. Stage 5 made it
// visible by turning `undefined + '/api/...'` into a relative URL that
// Netlify 404s. v80.12 attempted the fix using `window.DB.workerUrl()`
// but that's the same scope-prefix mistake — DB is `const`, not `var`,
// so it's in the global lexical scope, not on the global object. v80.13
// drops the `window.` prefix to match the established codebase
// convention (inline code uses bare `DB.method()` in 92 places).
//
// Public API consumed by inline HTML (currently exposed at window scope
// via the original inline copies; Phase 3 will switch HTML calls to use
// Top50.admin.* instead):
//   Top50.admin.switchTab(tab)
//   Top50.admin.wireDropZone()        — call from init, NOT auto-run
//   Top50.admin.setPreviewMetric(metric)
//   Top50.admin.commitUpload()
//   Top50.admin.cancelUpload()
//   Top50.admin.loadHistory()
//   Top50.admin.deleteReport(reportId, weekStart)

import { escHtml as esc, formatTop50Date } from './shared.js';
import { ensureXlsx } from '../shared/ensure-xlsx.js';   // v136.8: the spreadsheet library loads only when it is used
import { setHTML } from '../shared/set-html.js';   // v136.8: null-safe innerHTML for admin renders
import { adminToken } from '../shared/admin-token.js';   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)

// ── Module-level state (private to this module) ───────
let _top50Parsed = null;       // { weekStart, weekEnd, sourceFilename, entries[] }
let _top50PreviewMetric = 'value';

export function switchTab(tab) {
  document.getElementById('top50Upload').style.display  = tab === 'upload'  ? 'block' : 'none';
  document.getElementById('top50History').style.display = tab === 'history' ? 'block' : 'none';
  // v85.0 — store mapping sub-tab (alias review)
  var mappingEl = document.getElementById('top50Mapping');
  if (mappingEl) mappingEl.style.display = tab === 'mapping' ? 'block' : 'none';
  document.getElementById('top50TabUpload').className   = tab === 'upload'  ? 'btn btn-green' : 'btn btn-outline';
  document.getElementById('top50TabHistory').className  = tab === 'history' ? 'btn btn-green' : 'btn btn-outline';
  var mappingBtn = document.getElementById('top50TabMapping');
  if (mappingBtn) mappingBtn.className = tab === 'mapping' ? 'btn btn-green' : 'btn btn-outline';
  // v133.118 - Scan (src/scanAdmin): open it, and Head Office uploads its data
  var scanEl = document.getElementById('top50Scan');
  if (scanEl) scanEl.style.display = tab === 'scan' ? 'block' : 'none';
  var scanBtn = document.getElementById('top50TabScan');
  if (scanBtn) scanBtn.className = tab === 'scan' ? 'btn btn-green' : 'btn btn-outline';
  // v124.51: wire the drop zone here, not only at bundle load. adminPanels loads
  // in <head> and calls wireDropZone() before #top50DropZone exists (its guard
  // then silently bails → picker never opens). By the time the Upload tab is
  // shown the markup is present, so this reliably attaches the click→picker.
  if (tab === 'upload') wireDropZone();
  if (tab === 'history') loadHistory();
  if (tab === 'mapping') loadMapping();
  // v136.19: Scan has its own page (Trade, Scan); this sub-tab only points there
}

// Drop zone / file picker wiring. Called once on init (was an auto-running
// IIFE in the inline original). Idempotent — safe to call again, will just
// re-bind the same listeners.
export function wireDropZone() {
  var dz = document.getElementById('top50DropZone');
  var fi = document.getElementById('top50FileInput');
  if (!dz || !fi) return;
  if (dz.dataset.top50Wired) return;   // v124.51: idempotent — safe to call from both bundle-load init AND switchTab('upload')
  dz.dataset.top50Wired = '1';
  dz.addEventListener('click', function() { fi.click(); });
  fi.addEventListener('change', function(e) {
    if (e.target.files && e.target.files[0]) handleFile(e.target.files[0]);
  });
  dz.addEventListener('dragover', function(e) {
    e.preventDefault();
    dz.style.background = 'var(--adm-ok-pale)';
    dz.style.borderColor = 'var(--green)';
  });
  dz.addEventListener('dragleave', function() {
    dz.style.background = 'var(--surface-2)';
    dz.style.borderColor = 'var(--border)';
  });
  dz.addEventListener('drop', function(e) {
    e.preventDefault();
    dz.style.background = 'var(--surface-2)';
    dz.style.borderColor = 'var(--border)';
    if (e.dataTransfer.files && e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  });
}

async function handleFile(file) {
  var statusEl = document.getElementById('top50ParseStatus');
  statusEl.style.color = 'var(--sub)';
  statusEl.textContent = '⏳ Parsing ' + file.name + '...';

  try {
    await ensureXlsx();   // v136.8: loads on first use; a failed load lands in the catch below with a plain message
    var ab = await file.arrayBuffer();
    var wb = window.XLSX.read(ab, { type: 'array', cellDates: false });

    // Value sheet is required; Volume is optional but expected.
    if (!wb.SheetNames.includes('Value')) {
      throw new Error('No "Value" sheet found in workbook. Expected sheet names: Value, Volume.');
    }
    var valueSheet  = window.XLSX.utils.sheet_to_json(wb.Sheets['Value'],  { header: 1, raw: true, defval: null });
    var volumeSheet = wb.Sheets['Volume']
      ? window.XLSX.utils.sheet_to_json(wb.Sheets['Volume'], { header: 1, raw: true, defval: null })
      : null;

    var parsed = parseWorkbook(valueSheet, volumeSheet, file.name);
    _top50Parsed = parsed;

    statusEl.style.color = 'var(--green)';
    statusEl.textContent = '✓ Parsed — ' + parsed.entries.length + ' entries (' +
      parsed.entries.filter(function(e){return e.metric==='value';}).length + ' value, ' +
      parsed.entries.filter(function(e){return e.metric==='volume';}).length + ' volume) for week ' +
      formatTop50Date(parsed.weekStart) + ' → ' + formatTop50Date(parsed.weekEnd);

    showPreview();
  } catch (err) {
    console.error('[top50] parse failed:', err);
    statusEl.style.color = 'var(--red)';
    statusEl.textContent = '✗ Parse failed: ' + (err.message || err);
    _top50Parsed = null;
    document.getElementById('top50PreviewCard').style.display = 'none';
  }
}

// Convert one of {Value, Volume} sheets — given as 2D array — to entries.
// Layout (as analysed from the May 2026 sample file):
//   - row 0, col 3:  "Date:- DD/MM/YYYY - DD/MM/YYYY"
//   - row 0, cols 9, 11, 13, ...: store names (every 2 cols)
//   - row 3:         field headers
//                    Value:  [Sku, Item code, Description, Class UAE, Class Qatar,
//                             UAE WH SOH, Qatar WH SOH, Total Value, Total Qty,
//                             Sold Qty, SOH, Sold Qty, SOH, ...]
//                    Volume: same but no "Total Value" column → metadata is 8 cols, store data starts col 8
//   - row 4+:        data rows
function parseWorkbook(valueSheet, volumeSheet, sourceFilename) {
  // 1. Date range from Value sheet cell D1 (row 0, col 3 in zero-indexed AOA)
  var dateCell = valueSheet[0] && valueSheet[0][3];
  var dateMatch = String(dateCell || '').match(/(\d{2})\/(\d{2})\/(\d{4})\s*[-–]\s*(\d{2})\/(\d{2})\/(\d{4})/);
  if (!dateMatch) {
    throw new Error('Could not find date range in cell D1 of Value sheet (expected "Date:- DD/MM/YYYY - DD/MM/YYYY")');
  }
  // Convert DD/MM/YYYY → YYYY-MM-DD (ISO)
  var weekStart = dateMatch[3] + '-' + dateMatch[2] + '-' + dateMatch[1];
  var weekEnd   = dateMatch[6] + '-' + dateMatch[5] + '-' + dateMatch[4];

  // 2. Store names from Value sheet row 0, cols 9, 11, 13...
  // (Both Value and Volume share the same store columns; we trust Value.)
  // v131.82 (Head Office and an area manager, 16-17 Sep). This accepted ONLY columns starting
  // "ELC -", so when Head Office added "TE - ABU DHABI - AL REEM MALL" exactly as asked,
  // the parser skipped it silently: the column never reached store_data, so the
  // match audit reported the store as unmatched AND Reem never appeared in the
  // Top 50 dropdown. Two people chased a data problem that was a code problem.
  //
  // TE is a real brand in this estate (Al Reem Mall is the first TE store), and
  // the prefix is already the brand identifier everywhere else - the customer
  // survey has BRAND_PREFIXES = [te, elc] for exactly this. Silently dropping a
  // whole column was the worst possible failure: no error, no count, nothing on
  // screen to say a store had been ignored.
  // Spacing around the dash is DELIBERATELY tolerant. Reem's own upload_name was
  // stored as "TE- Abu Dhabi - Al Reem Mall" (no space after the dash) for weeks,
  // so a heading typed the same way in the sheet is entirely likely - and another
  // round-trip with Head Office over one space would be a worse outcome than accepting
  // it. Anything without a brand prefix is still rejected, and now LOGGED.
  var STORE_COL_RE = /^(ELC|TE)\s*-\s*\S/i;
  var stores = [];
  var skipped = [];
  for (var c = 9; c < (valueSheet[0] || []).length; c += 2) {
    var nm = valueSheet[0][c];
    if (typeof nm !== 'string' || !nm.trim()) continue;
    if (STORE_COL_RE.test(nm)) stores.push({ col: c, name: nm.trim() });
    else skipped.push(nm.trim());
  }
  if (stores.length === 0) {
    throw new Error('No store names found in row 1 of Value sheet (expected "ELC - ..." or "TE - ..." names every 2 columns starting col J)');
  }
  // Never drop a column in silence again: name what was ignored, so a heading
  // typo shows up at upload rather than as a store missing from a dropdown days
  // later.
  if (skipped.length) {
    console.warn('[top50] ignored ' + skipped.length + ' column heading(s) that are not ELC-/TE-prefixed: ' + skipped.join(' | '));
  }

  // 3. Parse entries from each metric sheet
  var entries = [];
  parseMetricSheet(valueSheet,  'value',  stores, /*metaCols=*/ 9, entries);
  if (volumeSheet) {
    // Volume sheet has 8 metadata cols (no "Total Value"), so store data starts at col 8
    parseMetricSheet(volumeSheet, 'volume', stores, /*metaCols=*/ 8, entries);
  }

  if (entries.length === 0) {
    throw new Error('No data rows found. Check that the file has products in row 5 onwards.');
  }

  return {
    weekStart: weekStart,
    weekEnd: weekEnd,
    sourceFilename: sourceFilename,
    entries: entries
  };
}

function parseMetricSheet(sheet, metric, stores, metaCols, entriesOut) {
  // Data starts at row 4 (zero-indexed). Iterate until we hit blank Sku.
  for (var r = 4; r < sheet.length; r++) {
    var row = sheet[r];
    if (!row || row[0] === null || row[0] === undefined || row[0] === '') continue;

    var rank = parseInt(row[0], 10);
    if (isNaN(rank)) continue;

    // Build store_data: { storeName: { qty, soh } } only including the
    // stores listed in row 0. Stores not present get omitted, not zero.
    // Adjust column offset for metric: Value has 9 metaCols (store data
    // starts col 9), Volume has 8 (store data starts col 8). Stores were
    // detected from Value's row 0, so their .col reference is the Value
    // sheet column. For Volume we shift by -1.
    var colShift = metric === 'value' ? 0 : -1;
    var storeData = {};
    stores.forEach(function(s) {
      var qtyCol = s.col + colShift;
      var sohCol = s.col + 1 + colShift;
      var qty = row[qtyCol];
      var soh = row[sohCol];
      // Coerce numerically; treat null/undefined as missing (skip).
      var qtyNum = (qty === null || qty === undefined || qty === '') ? null : Number(qty);
      var sohNum = (soh === null || soh === undefined || soh === '') ? null : Number(soh);
      if (qtyNum === null && sohNum === null) return;
      storeData[s.name] = {
        qty: qtyNum === null ? 0 : qtyNum,
        soh: sohNum === null ? 0 : sohNum
      };
    });

    var entry = {
      metric: metric,
      rank: rank,
      item_code: row[1] != null ? String(row[1]).trim() : null,
      description: row[2] != null ? String(row[2]).trim() : null,
      class_uae: row[3] != null ? String(row[3]).trim() : null,
      class_qatar: row[4] != null ? String(row[4]).trim() : null,
      uae_wh_soh: row[5] != null ? Number(row[5]) : null,
      qatar_wh_soh: row[6] != null ? Number(row[6]) : null,
      total_value: metric === 'value' && row[7] != null ? Number(row[7]) : null,
      // Total qty: Value sheet col 8, Volume sheet col 7
      total_qty: metric === 'value' ? (row[8] != null ? Number(row[8]) : null)
                                    : (row[7] != null ? Number(row[7]) : null),
      store_data: storeData
    };
    entriesOut.push(entry);
  }
}

function showPreview() {
  if (!_top50Parsed) return;
  document.getElementById('top50PreviewCard').style.display = 'block';
  document.getElementById('top50PreviewSummary').textContent =
    'Week ' + formatTop50Date(_top50Parsed.weekStart) + ' to ' + formatTop50Date(_top50Parsed.weekEnd) +
    ' · ' + _top50Parsed.entries.length + ' entries · ' +
    Object.keys(_top50Parsed.entries[0].store_data).length + ' stores';
  setPreviewMetric('value');
}

export function setPreviewMetric(m) {
  _top50PreviewMetric = m;
  document.getElementById('top50PreviewBtnValue').className  = m === 'value'  ? 'btn btn-sm' : 'btn btn-sm btn-outline';
  document.getElementById('top50PreviewBtnVolume').className = m === 'volume' ? 'btn btn-sm' : 'btn btn-sm btn-outline';
  // v136.8: --green-on, the green white text reads on (5.5:1; --green is 3.5:1)
  document.getElementById('top50PreviewBtnValue').style.background  = m === 'value'  ? 'var(--green-on)' : '';
  document.getElementById('top50PreviewBtnValue').style.color       = m === 'value'  ? '#fff' : '';
  document.getElementById('top50PreviewBtnVolume').style.background = m === 'volume' ? 'var(--green-on)' : '';
  document.getElementById('top50PreviewBtnVolume').style.color      = m === 'volume' ? '#fff' : '';
  renderPreviewTable();
}

function renderPreviewTable() {
  if (!_top50Parsed) return;
  var entries = _top50Parsed.entries.filter(function(e){ return e.metric === _top50PreviewMetric; });
  var html = '<table style="width:100%;border-collapse:collapse;font-size:12px">';
  html += '<thead><tr style="background:var(--green);color:#fff;position:sticky;top:0">';
  html += '<th style="padding:6px 8px;text-align:start">#</th>';
  html += '<th style="padding:6px 8px;text-align:start">Code</th>';
  html += '<th style="padding:6px 8px;text-align:start">Description</th>';
  html += '<th style="padding:6px 8px;text-align:end">' + (_top50PreviewMetric === 'value' ? 'Total Value' : 'Total Qty') + '</th>';
  // v126.83 (the Qatar area manager: "unable to see Qatar WH SOH") — the admin view never
  // showed EITHER warehouse; both markets, side by side, same as the store view.
  html += '<th style="padding:6px 8px;text-align:end">UAE WH</th>';
  html += '<th style="padding:6px 8px;text-align:end">Qatar WH</th>';
  html += '<th style="padding:6px 8px;text-align:start">Class UAE</th>';
  html += '<th style="padding:6px 8px;text-align:start">Class Qatar</th>';
  html += '</tr></thead><tbody>';
  entries.slice(0, 60).forEach(function(e) {
    html += '<tr style="border-bottom:1px solid var(--border)">';
    html += '<td style="padding:6px 8px">' + e.rank + '</td>';
    html += '<td style="padding:6px 8px;font-family:monospace">' + esc(e.item_code || '—') + '</td>';
    html += '<td translate="no" style="padding:6px 8px">' + esc(e.description || '—') + '</td>';   // v135.2: product text from the file, never translated
    var totalNum = _top50PreviewMetric === 'value' ? e.total_value : e.total_qty;
    html += '<td style="padding:6px 8px;text-align:end;font-variant-numeric:tabular-nums">' +
            (totalNum != null ? Number(totalNum).toLocaleString() : '—') + '</td>';
    html += '<td style="padding:6px 8px;text-align:end;font-variant-numeric:tabular-nums">' + (e.uae_wh_soh != null ? e.uae_wh_soh : '—') + '</td>';
    html += '<td style="padding:6px 8px;text-align:end;font-variant-numeric:tabular-nums">' + (e.qatar_wh_soh != null ? e.qatar_wh_soh : '—') + '</td>';
    html += '<td translate="no" style="padding:6px 8px">' + esc(e.class_uae || '—') + '</td>';
    html += '<td translate="no" style="padding:6px 8px">' + esc(e.class_qatar || '—') + '</td>';
    html += '</tr>';
  });
  html += '</tbody></table>';
  setHTML('top50PreviewTable', html);   // v136.8: null-safe (the box may not be on screen)
}

export async function commitUpload() {
  if (!_top50Parsed) return;
  var btn = document.getElementById('top50CommitBtn');
  btn.disabled = true;
  btn.textContent = '⏳ Uploading...';
  try {
    var res = await fetch(DB.workerUrl() + '/api/top50/upload', { cache: 'no-store',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + adminToken()
      },
      body: JSON.stringify({
        week_start: _top50Parsed.weekStart,
        week_end: _top50Parsed.weekEnd,
        source_filename: _top50Parsed.sourceFilename,
        entries: _top50Parsed.entries
      })
    });
    if (!res.ok) {
      var errBody = await res.text();
      throw new Error('Upload failed (HTTP ' + res.status + '): ' + errBody);
    }
    var result = await res.json();
    document.getElementById('top50ParseStatus').style.color = 'var(--green)';
    document.getElementById('top50ParseStatus').textContent =
      '✓ Uploaded — ' + result.entries_inserted + ' entries saved for week of ' +
      formatTop50Date(_top50Parsed.weekStart);
    cancelUpload();
    loadHistory();
    // v85.0 — after a successful upload, run the match audit. If any
    // canonical stores don't resolve to a spreadsheet key, auto-switch
    // to the Store Mapping tab so the admin sees what needs attention
    // immediately rather than discovering it days later when a manager
    // complains. Fail soft — never block the upload-success path.
    try {
      var auditRes = await fetch(DB.workerUrl() + '/api/top50/match-audit', { cache: 'no-store',
        headers: { 'Authorization': 'Bearer ' + adminToken() }
      });
      if (auditRes.ok) {
        var audit = await auditRes.json();
        if (audit && Array.isArray(audit.unmatched_canonical) && audit.unmatched_canonical.length > 0) {
          switchTab('mapping');
        }
      }
    } catch (e) {
      console.warn('[top50] post-upload audit check failed (non-fatal):', e);
    }
  } catch (err) {
    console.error('[top50] commit failed:', err);
    btn.disabled = false;
    btn.textContent = '✅ Commit Upload';
    alert('Upload failed: ' + err.message);
  }
}

export function cancelUpload() {
  _top50Parsed = null;
  document.getElementById('top50PreviewCard').style.display = 'none';
  document.getElementById('top50FileInput').value = '';
}

export async function loadHistory() {
  var listEl = document.getElementById('top50HistoryList');
  if (!listEl) return;
  listEl.innerHTML = '<div style="padding:20px;text-align:center;color:var(--sub)">Loading...</div>';
  try {
    var res = await fetch(DB.workerUrl() + '/api/top50/reports', { cache: 'no-store' });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    var reports = await res.json();
    if (!Array.isArray(reports) || reports.length === 0) {
      listEl.innerHTML = '<div style="padding:20px;text-align:center;color:var(--sub)">No reports uploaded yet.</div>';
      return;
    }
    var html = '<table style="width:100%;border-collapse:collapse;font-size:13px">';
    html += '<thead><tr style="background:var(--surface-2);border-bottom:2px solid var(--border)">';
    html += '<th style="padding:8px 10px;text-align:start">Week</th>';
    html += '<th style="padding:8px 10px;text-align:start">Uploaded</th>';
    html += '<th style="padding:8px 10px;text-align:start">By</th>';
    html += '<th style="padding:8px 10px;text-align:start">File</th>';
    html += '<th style="padding:8px 10px;text-align:end"></th>';
    html += '</tr></thead><tbody>';
    reports.forEach(function(r) {
      var uploadedDate = r.uploaded_at ? new Date(r.uploaded_at).toLocaleString('en-GB',{dateStyle:'medium',timeStyle:'short'}) : '—';
      html += '<tr style="border-bottom:1px solid var(--border)">';
      html += '<td style="padding:8px 10px">' + esc(formatTop50Date(r.week_start)) + ' → ' + esc(formatTop50Date(r.week_end)) + '</td>';
      html += '<td style="padding:8px 10px;color:var(--sub)">' + esc(uploadedDate) + '</td>';
      html += '<td translate="no" style="padding:8px 10px">' + esc(r.uploaded_by_name || '—') + '</td>';
      html += '<td style="padding:8px 10px;font-family:monospace;font-size:11px;color:var(--sub)">' + esc(r.source_filename || '—') + '</td>';
      html += '<td style="padding:8px 10px;text-align:end">';
      html += '<button class="btn btn-sm btn-outline" onclick="Top50.admin.deleteReport(' + r.id + ',\'' + esc(r.week_start) + '\')" style="color:var(--red);border-color:var(--red)">🗑 Delete</button>';
      html += '</td>';
      html += '</tr>';
    });
    html += '</tbody></table>';
    listEl.innerHTML = html;
  } catch (err) {
    console.error('[top50] history load failed:', err);
    listEl.innerHTML = '<div style="padding:20px;text-align:center;color:var(--red)">Failed to load: ' + esc(err.message) + '</div>';
  }
}

export async function deleteReport(reportId, weekStart) {
  if (!(await (window.uiConfirm || function (m) { return Promise.resolve(confirm(m)); })('Delete the Top 50 report for week of ' + formatTop50Date(weekStart) + '?\n\nThis removes it from the staff app immediately and cannot be undone.', { danger: true, confirmLabel: 'Delete' }))) return;
  try {
    var res = await fetch(DB.workerUrl() + '/api/top50/reports/' + reportId, { cache: 'no-store',
      method: 'DELETE',
      headers: { 'Authorization': 'Bearer ' + adminToken() }
    });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    loadHistory();
  } catch (err) {
    alert('Delete failed: ' + err.message);
  }
}

// ── v85.0 STORE MAPPING (Top 50 → canonical store alias review) ─────
//
// Why this exists: the Top 50 spreadsheet uses store names from a
// different source system than our rota/stores table. Most match
// cleanly after lowercase + "ELC -" prefix strip (the v80.4 fix in
// shared.js), but a handful drift further — e.g. "Dubai - Arabian
// Ranches 2" in stores is "ELC - DUBAI - ARABIAN RANCHES 2 COMMUNITY
// CENTRE" in the spreadsheet. These require an explicit alias.
//
// The mechanism (stores.upload_name column) already existed for
// daily/budget/addon uploads. v85.0 wires Top 50 through it and
// adds this admin UI for the per-upload review.
//
// Flow:
//   1. Admin uploads weekly Top 50 file (existing flow)
//   2. commitUpload() post-hook checks /api/top50/match-audit
//   3. If unmatched stores > 0, auto-switches to this tab
//   4. loadMapping() fetches audit + asks Claude Haiku to suggest matches
//   5. Admin picks the correct key from dropdown (AI suggestions ranked first)
//   6. saveAlias() PATCHes /admin/stores with upload_name
//   7. Reload — fewer unmatched. Repeat until clean.
//
// Persistence: once an alias is saved, future uploads with the same
// spreadsheet key auto-match. No re-entry needed unless the source
// system changes its naming.

export async function loadMapping() {
  var panel = document.getElementById('top50MappingContent');
  if (!panel) return;
  panel.innerHTML = '<div style="padding:20px;text-align:center;color:var(--sub)">Loading match audit…</div>';
  try {
    var token = adminToken();
    var res = await fetch(DB.workerUrl() + '/api/top50/match-audit', { cache: 'no-store',
      headers: { 'Authorization': 'Bearer ' + token }
    });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    var data = await res.json();

    if (!data.report) {
      panel.innerHTML = '<div style="padding:20px;text-align:center;color:var(--sub)">No Top 50 report uploaded yet. Upload one first to see mappings.</div>';
      return;
    }

    // Auto-suggest with Claude Haiku before rendering, so dropdowns
    // are pre-populated. Skip the call if there's nothing to suggest.
    var aiSuggestions = {};
    if (data.unmatched_canonical.length > 0 && data.orphan_keys.length > 0) {
      panel.innerHTML = '<div style="padding:20px;text-align:center;color:var(--sub)">⏳ Asking Claude Haiku to suggest matches for ' + data.unmatched_canonical.length + ' unmatched stores…</div>';
      try {
        aiSuggestions = await _aiSuggestUnmatched(
          data.unmatched_canonical.map(function(u){ return u.canonical; }),
          data.orphan_keys
        );
      } catch (e) {
        console.warn('[top50/mapping] AI suggest failed (continuing without):', e);
        aiSuggestions = {};
      }
    }

    _renderMappingPanel(panel, data, aiSuggestions);
  } catch (err) {
    console.error('[top50/mapping] load failed:', err);
    panel.innerHTML = '<div style="padding:20px;text-align:center;color:var(--red)">Failed to load: ' + esc(err.message) + '</div>';
  }
}

async function _aiSuggestUnmatched(unmatched, orphans) {
  var token = adminToken();
  var res = await fetch(DB.workerUrl() + '/api/top50/ai-suggest', { cache: 'no-store',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token
    },
    body: JSON.stringify({ unmatched_canonical: unmatched, orphan_keys: orphans })
  });
  if (!res.ok) throw new Error('AI suggest HTTP ' + res.status);
  var data = await res.json();
  return data.suggestions || {};
}

function _renderMappingPanel(panel, data, aiSuggestions) {
  var html = '';

  // Summary banner
  var totalCanonical = data.matched.length + data.unmatched_canonical.length;
  html += '<div style="background:var(--info-bg);border-left:4px solid #0369a1;padding:12px 16px;margin-bottom:16px;border-radius:0 8px 8px 0">';
  html += '<div style="font-size:13px;font-weight:600;color:var(--adm-sky-text)">Latest report: week of ' + esc(formatTop50Date(data.report.week_start)) + '</div>';
  html += '<div style="font-size:12px;color:var(--sub);margin-top:4px">';
  html += data.matched.length + ' of ' + totalCanonical + ' stores matched · ';
  html += data.unmatched_canonical.length + ' need an alias · ';
  html += data.orphan_keys.length + ' orphan keys in spreadsheet';
  html += '</div></div>';

  // Unmatched section (primary action area)
  if (data.unmatched_canonical.length > 0) {
    html += '<div style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;color:var(--warn-deep);margin-bottom:8px">⚠ Unmatched canonical stores</div>';
    html += '<div style="margin-bottom:24px">';
    data.unmatched_canonical.forEach(function(u) {
      var suggestions = aiSuggestions[u.canonical] || [];
      html += _renderUnmatchedRow(u, data.orphan_keys, suggestions);
    });
    html += '</div>';
  } else {
    html += '<div style="background:var(--adm-ok-pale);border-left:4px solid #15803d;padding:12px 16px;margin-bottom:24px;border-radius:0 8px 8px 0;font-size:13px;color:var(--ok);font-weight:600">✓ All canonical stores match a Top 50 key. Nothing to fix.</div>';
  }

  // Orphan keys (collapsed by default)
  if (data.orphan_keys.length > 0) {
    html += '<details style="margin-bottom:16px;border:1px solid var(--border);border-radius:6px;padding:8px 12px">';
    html += '<summary style="cursor:pointer;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;color:var(--sub)">📋 Orphan Top 50 keys — in spreadsheet, no canonical match (' + data.orphan_keys.length + ')</summary>';
    html += '<div style="padding:8px 0 0 0;font-family:monospace;font-size:12px;color:var(--sub);line-height:1.6">';
    data.orphan_keys.forEach(function(k) { html += '• ' + esc(k) + '<br>'; });
    html += '</div></details>';
  }

  // Matched (collapsed)
  html += '<details style="border:1px solid var(--border);border-radius:6px;padding:8px 12px">';
  html += '<summary style="cursor:pointer;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;color:var(--sub)">✓ Matched stores (' + data.matched.length + ')</summary>';
  html += '<table style="width:100%;border-collapse:collapse;font-size:12px;margin-top:8px">';
  html += '<thead><tr style="background:var(--surface-2);border-bottom:1px solid var(--border)"><th style="padding:6px 8px;text-align:start">Store</th><th style="padding:6px 8px;text-align:start">Top 50 key</th><th style="padding:6px 8px;text-align:start">Match via</th></tr></thead><tbody>';
  data.matched.forEach(function(m) {
    var typeBadge = (m.matched_via && m.matched_via.indexOf('alias') === 0)
      ? '<span style="background:var(--adm-info-tint);color:var(--info-deep);padding:2px 6px;border-radius:4px;font-size:10px">alias</span>'
      : '<span style="background:var(--surface-3);color:var(--sub);padding:2px 6px;border-radius:4px;font-size:10px">normalised</span>';
    html += '<tr style="border-bottom:1px solid var(--border)">';
    html += '<td translate="no" style="padding:6px 8px">' + esc(m.canonical) + '</td>';
    html += '<td style="padding:6px 8px;font-family:monospace;font-size:11px">' + esc(m.key) + '</td>';
    html += '<td style="padding:6px 8px">' + typeBadge + '</td></tr>';
  });
  html += '</tbody></table></details>';

  panel.innerHTML = html;
}

function _renderUnmatchedRow(unmatched, allOrphans, suggestions) {
  var safeId = 'mapping-row-' + unmatched.canonical.replace(/[^a-zA-Z0-9]/g, '_');
  var html = '<div id="' + safeId + '" style="background:var(--card);border:1px solid var(--adm-warn-line);border-radius:8px;padding:14px;margin-bottom:10px">';
  html += '<div style="display:flex;align-items:flex-start;justify-content:space-between;gap:14px;flex-wrap:wrap">';
  // Left column: store info
  html += '<div style="flex:1 1 260px">';
  html += '<div translate="no" style="font-weight:600;font-size:14px">' + esc(unmatched.canonical) + '</div>';
  if (unmatched.country) {
    html += '<div style="font-size:11px;color:var(--sub);margin-top:2px">' + esc(unmatched.country) + '</div>';
  }
  if (unmatched.current_upload_name) {
    html += '<div style="font-size:11px;color:var(--sub);margin-top:6px">Current alias: <code style="background:var(--warn-bg);padding:1px 4px;border-radius:3px">' + esc(unmatched.current_upload_name) + '</code></div>';
    html += '<div style="font-size:11px;color:var(--warn-deep);margin-top:2px">(doesn\'t match any key in this week\'s upload)</div>';
  } else {
    html += '<div style="font-size:11px;color:var(--sub);margin-top:6px">No alias set yet</div>';
  }
  html += '</div>';
  // Right column: dropdown + save
  html += '<div style="flex:2 1 360px">';
  html += '<select class="t50-mapping-select" data-canonical="' + esc(unmatched.canonical) + '" style="width:100%;padding:8px;border:1px solid var(--border);border-radius:4px;font-size:12px;background:var(--card)">';
  html += '<option value="">— Select spreadsheet key —</option>';

  // AI suggestions first
  var seenKeys = Object.create(null);
  if (suggestions.length > 0) {
    html += '<optgroup label="✨ AI suggestions">';
    suggestions.forEach(function(s) {
      if (s.key && allOrphans.indexOf(s.key) !== -1 && !seenKeys[s.key]) {
        seenKeys[s.key] = true;
        var conf = Math.round((s.confidence || 0) * 100);
        html += '<option value="' + esc(s.key) + '">' + esc(s.key) + '  ·  ' + conf + '% confident</option>';
      }
    });
    html += '</optgroup>';
  }
  // Other orphans
  var otherOrphans = allOrphans.filter(function(k){ return !seenKeys[k]; });
  if (otherOrphans.length > 0) {
    html += '<optgroup label="Other orphan keys">';
    otherOrphans.forEach(function(k) {
      html += '<option value="' + esc(k) + '">' + esc(k) + '</option>';
    });
    html += '</optgroup>';
  }
  // Clear action
  html += '<optgroup label="Other actions">';
  html += '<option value="__CLEAR__">⊘ Clear alias (leave unmatched)</option>';
  html += '</optgroup>';
  html += '</select>';

  // AI reason (top suggestion only)
  if (suggestions.length > 0 && suggestions[0].reason) {
    html += '<div style="font-size:11px;color:var(--sub);margin-top:6px;font-style:italic">💡 <span translate="no">' + esc(suggestions[0].reason) + '</span></div>';
  }

  html += '<button class="btn btn-sm btn-green" style="margin-top:10px" onclick="Top50.admin.saveAlias(\'' + safeId + '\')">💾 Save mapping</button>';
  html += '</div></div></div>';
  return html;
}

export async function saveAlias(rowId) {
  var row = document.getElementById(rowId);
  if (!row) return;
  var select = row.querySelector('.t50-mapping-select');
  if (!select) return;
  var canonical = select.dataset.canonical;
  var key = select.value;
  if (!canonical || !key) {
    alert('Select a key (or "Clear alias") first.');
    return;
  }
  // Magic value: __CLEAR__ means set upload_name to NULL.
  var aliasToSave = (key === '__CLEAR__') ? null : key;

  var btn = row.querySelector('button');
  if (btn) { btn.disabled = true; btn.textContent = '⏳ Saving...'; }

  try {
    var token = adminToken();
    var res = await fetch(DB.workerUrl() + '/admin/stores?name=' + encodeURIComponent(canonical), { cache: 'no-store',
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      },
      body: JSON.stringify({ upload_name: aliasToSave })
    });
    if (!res.ok) {
      var errText = await res.text();
      throw new Error('HTTP ' + res.status + ': ' + errText);
    }
    // Refresh — newly-matched store moves out of unmatched list.
    loadMapping();
  } catch (err) {
    console.error('[top50/mapping] save failed:', err);
    alert('Save failed: ' + err.message);
    if (btn) { btn.disabled = false; btn.textContent = '💾 Save mapping'; }
  }
}
